import { ReactNode, useEffect } from 'react';
import { IntercomProvider, useIntercom } from 'react-use-intercom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { FacebookProvider } from 'react-facebook';
import { ToastContainer } from 'react-toastify';
import { useRouter } from 'next/router';

import { ReactQueryWrapper, ServiceProvider } from '@restworld/data-services';
import { AuthProvider, tokenKeyName, useAuth } from '@restworld/feature-authentication';

import { GlobalOptionsFetchingProvider, UTMSourceProvider, ToastMessageContextProvider, AnalyticsProvider } from './';
import { IubendaProvider } from './iubenda';
import { defaultToastConfig } from '../constants/default-toast-config';
import { useIsEmployerApp } from '../hooks';
import { useIntercomBootProps } from '../hooks/use-intercom-boot-props';

const baseUrl = process.env.NEXT_PUBLIC_BASE_API_URL ?? '';
const INTERCOM_APP_ID = process.env.NEXT_PUBLIC_INTERCOM_APP_ID ?? '';
const googleClientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ?? '';
const facebookAppId = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID ?? '';

export const MainContext = ({ children }: { children: React.ReactNode }) => (
  <ReactQueryWrapper>
    <AuthProvider baseUrl={baseUrl}>
      <ServiceProviderWrapper>
        <IubendaProvider>
          <GoogleOAuthProvider clientId={googleClientId}>
            <FacebookProvider appId={facebookAppId}>
              <IntercomServiceProvider>
                <AnalyticsProvider>
                  <UTMSourceProvider>
                    <GlobalOptionsFetchingProvider>
                      <ToastMessageContextProvider>{children}</ToastMessageContextProvider>
                      <ToastContainer {...defaultToastConfig} />
                    </GlobalOptionsFetchingProvider>
                  </UTMSourceProvider>
                </AnalyticsProvider>
              </IntercomServiceProvider>
            </FacebookProvider>
          </GoogleOAuthProvider>
        </IubendaProvider>
      </ServiceProviderWrapper>
    </AuthProvider>
  </ReactQueryWrapper>
);
export const IntercomServiceProvider = ({ children }: { children: ReactNode }) => (
  <IntercomProvider appId={INTERCOM_APP_ID} initializeDelay={500}>
    <IntercomInitiation>{children}</IntercomInitiation>
  </IntercomProvider>
);

const IntercomInitiation = ({ children }: { children?: ReactNode }) => {
  const { boot, shutdown } = useIntercom();
  const { user } = useAuth();
  const router = useRouter();
  const { isEmployerApp } = useIsEmployerApp();
  // The token travels inside `customAttributes` because react-use-intercom v5 predates
  // `intercom_user_jwt` and silently drops any boot prop it does not recognise, while
  // `customAttributes` is spread verbatim into the raw Intercom settings. See the helper for why
  // the id and the email are sent either inside the token or beside it, never both, and the hook
  // for why this value must not change identity on every navigation.
  const autoBootProps = useIntercomBootProps(user, isEmployerApp, router.pathname);

  useEffect(() => {
    if (typeof window !== 'undefined') boot({ ...autoBootProps });
    return () => {
      shutdown();
    };
  }, [autoBootProps, boot, shutdown]);
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};
export const ServiceProviderWrapper = ({ children }: WrapperProps) => {
  const { jwt: token } = useAuth();
  const jwt = token || typeof localStorage !== 'undefined' ? localStorage.getItem(tokenKeyName) : '';
  return (
    <ServiceProvider baseUrl={baseUrl} jwt={jwt as string}>
      {children}
    </ServiceProvider>
  );
};
interface WrapperProps {
  children: ReactNode;
}
